import FeatureDialog from 'stories/elements/Modal/DialogWindow/FeatureDialog'
import styled from 'styled-components'
import NewButton from 'components/elements/NewButton'
import _useTranslation from 'lib/hooks/_useTranslation'
import { Body1, Body2, ButtonText, Headline3 } from 'stories/elements/Typography/Text'
import theme from 'stories/utils/theme'
import BannerLogo from 'assets/app/knows/banner_logo.png'
import PlusPlanPriceLabel from './PlusPlanPriceLabel'
import CloseIcon from 'assets/icons/close.svg'
import { proPlanOptionRoute } from 'lib/constants/routes'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/reducer'
import { useToggleState } from 'lib/hooks/useToggleState'
import BenefitsTable from 'assets/app/knows/benefits-table.png'
import {
    AnalyticsEventProPlanMonetizationRedirectDialogBenefitsClick,
    AnalyticsEventProPlanMonetizationRedirectDialogClick,
    AnalyticsEventProPlanMonetizationRedirectDialogClose,
} from 'lib/constants/AnalyticsEventType'
import { trackEvent } from 'lib/tracking/analytics-service'
import { useRouter } from 'next/router'
import NextImage from 'components/elements/NextImage'
import { KnowunityNumbers } from 'lib/constants/KnowunityNumbers'

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: linear-gradient(90deg, #50b354 0%, #0075c2 100%);
    padding: 25px;
    position: relative;
    .stack-gutter {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        width: 100%;
        gap: 15px;
    }
`

const CloseButton = styled.button`
    position: absolute;
    top: 15px;
    right: 15px;
    background: transparent;
    border: none;
    font-size: 24px;
    cursor: pointer;
    svg path {
        fill: ${theme.colors.white};
    }
`

const UserLabel = styled.div`
    width: 100%;
    padding: 50px 0 15px 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    p {
        white-space: nowrap;
    }
    span {
        height: 1px;
        border: 1px solid rgb(209, 213, 219, 0.2);
        width: 100%;
    }
`

const Logo = styled.img`
    position: absolute;
    top: 15px;
    left: 15px;
`

const Content = styled.div<{ showBenefits?: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 25px;
    padding: ${(props) => (props.showBenefits ? '0' : '25px 0')};
    .benefits {
        position: absolute;
        display: grid;
        gap: 22.5px;
        width: 350px;
        top: 293px;
        left: 25px;
        p {
            font-size: 16px;
            font-family: var(--font-open-sans);
            font-weight: 400;
            color: ${theme.colors.white};
        }
    }
`

interface Props {
    isOpen: boolean
    onClose: () => void
    title: string
    image: StaticImageData
    type: 'download' | 'matching-content'
}

const MonetizationRedirectDialog = ({ isOpen, onClose, title, image, type }: Props) => {
    const [showBenefits, toggleBenefits] = useToggleState(false)
    const { t } = _useTranslation('plusPlan')
    const authenticatedUser = useSelector((state: AppState) => state.user)
    const router = useRouter()

    const onSubscribeButtonClick = () => {
        trackEvent(AnalyticsEventProPlanMonetizationRedirectDialogClick, { value: type })
        router.push(proPlanOptionRoute({ section: 'checkout', referrerScreen: type }))
    }

    const onBenefitsClick = () => {
        trackEvent(AnalyticsEventProPlanMonetizationRedirectDialogBenefitsClick, { value: type })
        toggleBenefits()
    }

    const onCloseClick = () => {
        trackEvent(AnalyticsEventProPlanMonetizationRedirectDialogClose, { value: type })
        showBenefits && toggleBenefits()
        onClose()
    }

    return (
        <FeatureDialog
            open={isOpen}
            onClose={onCloseClick}
            title="KnowunityPro"
            ariaPrefix="monetization-redirect-dialog"
            hideTitle
            fullNoPadding
            maxWidth={600}
            allowVerticalOverflow
        >
            <Container>
                <CloseButton onClick={onCloseClick}>
                    <CloseIcon />
                </CloseButton>
                <Logo src={BannerLogo.src} alt="KnowunityPro" width={150} />

                <UserLabel>
                    <span />
                    <Body2 color={theme.colors.white} fontWeight={400} fontSize={16} fontFamily="var(--font-inter)">
                        {t('plusPlan/monetizationDialogFor', {
                            name: authenticatedUser?.name ?? authenticatedUser?.username,
                        })}
                    </Body2>
                    <span />
                </UserLabel>

                <Headline3 fontFamily="var(--font-open-sans)" color={theme.colors.white} fontWeight={600}>
                    {title}
                </Headline3>

                <div className="stack-gutter">
                    <Content showBenefits={showBenefits}>
                        <>
                            {showBenefits ? (
                                <>
                                    <NextImage
                                        src={BenefitsTable.src}
                                        alt="Content Matching"
                                        width={BenefitsTable.width}
                                        height={370}
                                        quality={100}
                                        objectFit="contain"
                                    />
                                    <div className="benefits">
                                        <Body1>
                                            {t('plusPlan/packageBenefitText1', {
                                                knowThousandsCount: KnowunityNumbers.knowThousandsCount,
                                            })}
                                        </Body1>
                                        <Body1>{t('plusPlan/packageBenefitText2')}</Body1>
                                        <Body1>{t('plusPlan/packageBenefitText3')}</Body1>
                                        <Body1>{t('plusPlan/packageFeatureText4')}</Body1>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <Body2 color={theme.colors.white} fontWeight={400} fontSize={17}>
                                        {t('plusPlan/monetizationDialogReleventKnowText')}
                                    </Body2>

                                    <img src={image.src} alt="Content Matching" width={image.width} height={image.height} />
                                </>
                            )}
                        </>
                    </Content>

                    <PlusPlanPriceLabel fullWidth />

                    <NewButton
                        borderRadius={theme.borderRadius.normal}
                        color={theme.colors.white}
                        onClick={onSubscribeButtonClick}
                        fullWidth
                        width={550}
                    >
                        <ButtonText color={theme.colors.businessBlue} fontFamily="var(--font-open-sans)" fontWeight={700}>
                            {t('plusPlan/monetizationDialogSubscribeNow')}
                        </ButtonText>
                    </NewButton>

                    {!showBenefits ? (
                        <NewButton
                            width={550}
                            borderRadius={theme.borderRadius.normal}
                            onClick={onBenefitsClick}
                            color={theme.colors.transparent}
                            height={30}
                        >
                            <ButtonText color={theme.colors.white} fontFamily="var(--font-open-sans)" fontWeight={700}>
                                {t('plusPlan/monetizationDialogViewAllBenefits')}
                            </ButtonText>
                        </NewButton>
                    ) : null}
                    <Body2
                        fontFamily="var(--font-open-sans)"
                        fontSize={13}
                        color={`${theme.colors.white}dd`}
                        fontWeight={400}
                        style={{ textAlign: 'center' }}
                    >
                        {t('plusPlan/monetizationDialogBilledOnceAYear')}
                    </Body2>
                </div>
            </Container>
        </FeatureDialog>
    )
}

export default MonetizationRedirectDialog
